import { IApiModifyResponse } from '@/types/api';
import { AxiosResponse } from 'axios';
import { login, logout, registration, getRegistration, forgotPassword, resetPassword, sendVerifyEmail, emailVerify } from '@/api/auth';
import { AppError } from '@/errors/appError';
import { v4 as uuidv4 } from 'uuid';
import i18n from '@/plugins/i18n';
import { IGuestParams, ILoginParams, IRegistrationParams, IForgotPasswordParams, ISendVerifyEmailParams, IGetRegistrationParams, IResetPasswordParams, IVerifyEmailParams } from '@/types';
import { Currency, Platform, UserType } from '@/constants';
import { getPlatform } from '@/helpers/getPlatform';
import { useUserStore } from '@/stores/user';
import { useMenuStore } from '@/stores/menu';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import { currencySymbolDictionary } from '@/stores/simpleDictionary/currencySymbol';
import { globals } from '@/globals';
import { getWebSocketURL } from '@/helpers';
import { setRemoteDictionaries } from '@/helpers/dictionary';
import { usePlatform } from '@/composables/usePlatform'

const AuthService = {
    async logout() {
        const menuStore = useMenuStore();
        try {
            const response = await logout();
            if (response.data.success) {
                const appStore = useAppStore();

                localStorage.removeItem('refreshTime');
                localStorage.removeItem('user');
                localStorage.removeItem('token');

                if (globals.$socket && globals.$socket.readyState) {
                    globals.$disconnect();
                }

                menuStore.withMenu = false;

                if (getPlatform() !== Platform.Tradeball) {
                    const params: IGuestParams = {
                        ccy: appStore.ccy,
                        lang: appStore.lang,
                    };
                    appStore.resetState();
                    appStore.uniqAppId = uuidv4();

                    this.guest(params);

                    if (globals.router.currentRoute.value.name !== 'feedLive') {
                        await globals.router.push({ name: 'feedLive' });
                        location.reload();
                    } else {
                        location.reload();
                    }
                } else {
                    localStorage.removeItem('user');
                    appStore.resetState();
                    appStore.uniqAppId = uuidv4();
                    await globals.router.push({ name: 'login' });
                }
            } else {
                const authStore = useAuthStore();
                authStore.errorMessage = response.data.message;
            }
        } catch (e) {
            throw new AppError(`Unable to logout, request error`, { cause: e });
        } finally {
        }
    },

    guest(params: IGuestParams) {
        const userStore = useUserStore();
        const menuStore = useMenuStore();
        const platform = usePlatform();

        let currencyId: number = platform.isAggregator() ? Currency.Euro : Currency.Brl;
        let timeZone = 'America/Sao_Paulo';
        const ccy = params?.ccy?.toLowerCase();
        if (ccy === 'eur') {
            currencyId = Currency.Euro;
            timeZone = 'Europe/London';
        } else if (ccy === 'usd') {
            currencyId = Currency.Usd;
            timeZone = 'Europe/London';
        }

        userStore.token = null;
        userStore.login = 'Guest';
        userStore.type = UserType.Guest;
        userStore.currency.id = currencyId;
        userStore.currency.symbol = currencySymbolDictionary.nameById(currencyId);
        userStore.timeZone = timeZone;
        userStore.lang = params.lang;

        menuStore.withMenu = true;

        const authStore = useAuthStore();
        authStore.errorMessage = '';
    },

    async login(params: ILoginParams) {
        const appStore = useAppStore();
        const userStore = useUserStore();
        const menuStore = useMenuStore();
        const authStore = useAuthStore();

        const response = await login({
            username: params.username,
            password: params.password,
            os: params.os,
            browser: params.browser,
            token: params.token,
        });

        if (response.data.success) {
            const user = JSON.parse(response.data.user);

            userStore.token = response.data.token;
            userStore.login = user.login;
            userStore.type = user.type;
            userStore.currency.id = user.currency;
            userStore.currency.symbol = currencySymbolDictionary.nameById(user.currency);
            userStore.timeZone = user.timeZone;
            userStore.ownerId = user.owner_id;

            authStore.errorMessage = '';
            authStore.isShowSendVerifyEmailLink = false;
            menuStore.withMenu = true;
            if (globals.$socket && globals.$socket.readyState) {
                globals.$disconnect();
            }
            globals.$connect(getWebSocketURL(response.data.token));

            if (getPlatform() !== Platform.Tradeball) {
                appStore.lang = params.lang; //todo: need to check where we use appStore.lang
                appStore.ccy = params.ccy;
                window.addEventListener('message', (event) => {
                    if (event.data?.type === 'userBalance') {
                        const credit = parseFloat(event.data.value);
                        userStore.updateUserCredit({ credit });
                    }
                });
            }
            if (localStorage.getItem('isStatsAdded') === null) {
                // ...stats initialization as before...
                localStorage.setItem('isStatsAdded', 'true');
            }
        } else {
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            if (getPlatform() !== Platform.Tradeball) {
                this.guest({ lang: params.lang, ccy: params.ccy });
            } else {
                const t = i18n.global.t;
                authStore.errorMessage = t(response.data.message);
                authStore.isShowSendVerifyEmailLink = response.data.message === 'login.notVerified';
            }
        }
        return response;
    },

    async getRegistration(params: IGetRegistrationParams) {
        const authStore = useAuthStore();

        try {
            const response = await getRegistration(params);
            if (response.data.success) {
                if (response.data.dictionary) setRemoteDictionaries(response.data.dictionary);
            } else {
                authStore.errorMessage = response.data.message;
            }
            return response;
        } catch (error) {
            console.error('getRegistration error:', error);
        }
    },

    async registration(params: IRegistrationParams): Promise<AxiosResponse<IApiModifyResponse>> {
        try {
            return await registration(params);
        } catch (e) {
            throw new AppError(`Unable to register new player, request error`, { cause: e });
        } finally {
        }
    },

    async forgotPassword(params: IForgotPasswordParams): Promise<AxiosResponse<IApiModifyResponse>> {
        try {
            return await forgotPassword(params);
        } catch (e) {
            throw new AppError(`Unable to send forgot password request, request error`, { cause: e });
        } finally {
        }
    },

    async resetPassword(params: IResetPasswordParams): Promise<AxiosResponse<IApiModifyResponse>> {
        try {
            return await resetPassword(params);
        } catch (e) {
            throw new AppError(`Unable to reset password, request error`, { cause: e });
        } finally {
        }
    },

    async sendVerifyEmail(params: ISendVerifyEmailParams): Promise<AxiosResponse<IApiModifyResponse>> {
        try {
            const response = await sendVerifyEmail(params);
            if (response.data.success) {
                const authStore = useAuthStore();
                authStore.errorMessage = '';
            }

            return response;
        } catch (e) {
            throw new AppError(`Unable to send verify email, request error`, { cause: e });
        } finally {
        }
    },

    async emailVerify(params: IVerifyEmailParams): Promise<AxiosResponse<IApiModifyResponse>> {
        try {
            return await emailVerify(params);
        } catch (e) {
            throw new AppError(`Unable to verify email, request error`, { cause: e });
        } finally {
        }
    },
};

export default AuthService;
